import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, Pill, StatTile } from '../components/ui'
import type { PillTone } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror /api/v1/markdowns brand-grouped response) ────────────────────
interface Location { id: string; name: string }
export interface SkuRow {
  competitor_id: string
  competitor_name: string
  product: string
  brand: string | null
  category: string
  original_price: number
  price: number
  pct_off: number
  dollar_off: number
  discount_label: string | null
  collected_at: string
  distance_mi: number | null
  your_price: number | null
  vs_you_dollar: number | null // rival − you; negative = rival cheaper
  vs_you_pct: number | null
  rival_cheaper: boolean
}
interface BrandGroup {
  brand: string | null
  brand_label: string
  categories: string[]
  operator_carries: boolean
  rivals_cheaper_than_you: number
  discounted_rival_count: number
  best_pct_off: number
  best_dollar_off: number
  sku_count: number
  latest_collected_at: string
  skus: SkuRow[]
}
interface RivalAgg { competitor_id: string; competitor_name: string; count: number }
interface WholeBrand { competitor_id: string; competitor_name: string; brand: string; count: number }
interface Kpis {
  brands_discounted: number
  rivals_discounting: number
  you_carry_and_undercut: number
  biggest_drop: { pct: number; dollar: number; product: string; competitor_name: string } | null
}
interface MdData {
  has_own_menu: boolean
  total_rivals: number
  kpis: Kpis
  brand_groups: BrandGroup[]
  by_rival: RivalAgg[]
  categories: string[]
  brands: string[]
  whole_brand: WholeBrand[]
  total_skus: number
  total_brands: number
  filtered_skus: number
  filtered_brands: number
}
type Sort = 'undercut' | 'pct' | 'dollar' | 'newest'
type Show = 'carried' | 'all'

const SORT_LABEL: Record<Sort, string> = {
  undercut: 'Rivals undercutting you',
  pct: 'Biggest % off',
  dollar: 'Biggest $ off',
  newest: 'Newest',
}

const fmtMoney = (n: number) =>
  '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtSigned = (n: number) => (n < 0 ? '−' : '+') + fmtMoney(Math.abs(n))
const fmtMi = (d: number | null) => (d != null ? `${d.toFixed(1)}mi` : '')
const ageLabel = (iso: string) => {
  const hours = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000))
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

// ─── Small self-contained dropdown ──────────────────────────────────────────
function Dropdown({ label, value, children }: { label: string; value: string; children: (close: () => void) => React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ position: 'relative' }} ref={ref}>
        <button
          onClick={() => setOpen(v => !v)}
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-1)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
        >
          {value}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 9, height: 9, opacity: 0.55 }}><polyline points="6 9 12 15 18 9" /></svg>
        </button>
        {open && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, minWidth: 170, maxHeight: 320, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
            {children(() => setOpen(false))}
          </div>
        )}
      </div>
    </div>
  )
}
function Opt({ children, selected, onClick }: { children: React.ReactNode; selected?: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{ padding: '7px 12px', fontSize: 11, cursor: 'pointer', color: selected ? 'var(--accent)' : 'var(--text-1)', background: selected ? 'rgba(9,161,161,0.08)' : 'transparent', borderLeft: selected ? '2px solid var(--accent)' : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
    >{children}</div>
  )
}

// ─── Segmented toggle (Brands you carry / All brands) ───────────────────────
function Segmented({ value, onChange, options, disabled }: {
  value: string; onChange: (v: string) => void
  options: { value: string; label: string }[]; disabled?: boolean
}) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 2, opacity: disabled ? 0.45 : 1 }}>
      {options.map(o => {
        const active = value === o.value
        return (
          <button
            key={o.value}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            style={{
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? '#fff' : 'var(--text-2)',
              border: 'none', borderRadius: 4, padding: '4px 10px',
              fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600,
              cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
            }}
          >{o.label}</button>
        )
      })}
    </div>
  )
}

// ─── Brand status badge ─────────────────────────────────────────────────────
function StatusBadge({ g, hasOwnMenu }: { g: BrandGroup; hasOwnMenu: boolean }) {
  if (!hasOwnMenu) return null // no own menu → we can't claim a carry/undercut state
  let tone: PillTone
  let text: string
  if (g.operator_carries && g.rivals_cheaper_than_you > 0) {
    tone = 'danger'
    text = `${g.rivals_cheaper_than_you} rival${g.rivals_cheaper_than_you === 1 ? '' : 's'} cheaper than you`
  } else if (g.operator_carries) {
    tone = 'shared'
    text = 'not undercut'
  } else {
    tone = 'neutral'
    text = "you don't carry"
  }
  return <Pill tone={tone} variant="pill" style={{ fontWeight: 600 }}>{text}</Pill>
}

// ─── vs-you cell ────────────────────────────────────────────────────────────
function VsYou({ s }: { s: SkuRow }) {
  if (s.your_price == null || s.vs_you_dollar == null) {
    return <span style={{ color: 'var(--text-3)' }}>—</span>
  }
  const cheaper = s.rival_cheaper
  return (
    <span style={{ fontFamily: 'var(--mono)', color: cheaper ? 'var(--danger)' : 'var(--text-2)', fontWeight: cheaper ? 700 : 500, whiteSpace: 'nowrap' }}>
      {fmtSigned(s.vs_you_dollar)}
    </span>
  )
}

// ─── Expandable brand row ───────────────────────────────────────────────────
function BrandRow({ g, hasOwnMenu, totalRivals, open, onToggle }: {
  g: BrandGroup; hasOwnMenu: boolean; totalRivals: number; open: boolean; onToggle: () => void
}) {
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <button
        data-testid="pricedrops-brand-row"
        onClick={onToggle}
        style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}
      >
        {/* Left: brand + categories + status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
          <span style={{ fontFamily: 'var(--display)', fontSize: 18, color: 'var(--text-1)', lineHeight: 1.1, whiteSpace: 'nowrap' }}>{g.brand_label}</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {g.categories.slice(0, 3).map(c => <Pill key={c} tone="neutral" variant="chip">{c}</Pill>)}
          </div>
          <StatusBadge g={g} hasOwnMenu={hasOwnMenu} />
        </div>
        {/* Right: coverage + best drop + chevron */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
            discounted at {g.discounted_rival_count}/{totalRivals} rival{totalRivals === 1 ? '' : 's'}
          </span>
          <Pill tone="danger" variant="chip" style={{ fontWeight: 700 }}>best −{g.best_pct_off < 1 ? '<1' : g.best_pct_off}%</Pill>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ width: 12, height: 12, color: 'var(--text-3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Product', 'Rival', 'Dist', 'Was → Now', '% off', 'vs you'].map((h, i) => (
                  <th key={h} style={{ textAlign: i >= 2 ? 'right' : 'left', padding: '7px 14px', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {g.skus.map((s, i) => (
                <tr key={s.competitor_id + '|' + s.product + '|' + i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 14px', color: 'var(--text-1)' }}>{s.product}</td>
                  <td style={{ padding: '8px 14px', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{s.competitor_name}</td>
                  <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--mono)', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{fmtMi(s.distance_mi) || '—'}</td>
                  <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>
                    <span style={{ color: 'var(--text-3)', textDecoration: 'line-through' }}>{fmtMoney(s.original_price)}</span>
                    <span style={{ color: 'var(--text-3)', margin: '0 4px' }}>→</span>
                    <span style={{ color: 'var(--text-1)', fontWeight: 700 }}>{fmtMoney(s.price)}</span>
                  </td>
                  <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                    <Pill tone="danger" variant="chip" style={{ fontWeight: 700 }}>−{s.pct_off < 1 ? '<1' : s.pct_off}%</Pill>
                  </td>
                  <td style={{ padding: '8px 14px', textAlign: 'right' }}><VsYou s={s} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {g.sku_count > g.skus.length && (
            <div style={{ padding: '8px 14px', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', borderTop: '1px solid var(--border)' }}>
              Showing {g.skus.length} of {g.sku_count} marked-down SKUs — filter by rival or category to see the rest.
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

// ─── Component ─────────────────────────────────────────────────────────────
export default function MarkdownsView({ locations, selectedLocation, onSelectLocation, authFetch }: {
  locations: Location[]
  selectedLocation: string | null
  onSelectLocation: (id: string) => void
  authFetch: (input: string, init?: RequestInit) => Promise<Response>
}) {
  const [sort, setSort] = useState<Sort>('undercut')
  const [show, setShow] = useState<Show>('carried')
  const [rival, setRival] = useState('')
  const [category, setCategory] = useState('')
  const [brand, setBrand] = useState('')
  const [wholeBrandOnly, setWholeBrandOnly] = useState(false)
  const [data, setData] = useState<MdData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Reset filters + expansion when the location changes.
  useEffect(() => { setRival(''); setCategory(''); setBrand(''); setWholeBrandOnly(false); setExpanded(new Set()) }, [selectedLocation])

  const load = useCallback(() => {
    if (!selectedLocation) return
    setLoading(true)
    setError(null)
    const qs = new URLSearchParams({ location_id: selectedLocation, sort })
    if (rival) qs.set('rival', rival)
    if (category) qs.set('category', category)
    if (brand) qs.set('brand', brand)
    authFetch(`${API}/api/v1/markdowns?${qs.toString()}`)
      .then(r => {
        if (!r.ok) return r.json().then((d: any) => { throw new Error(d.error || `HTTP ${r.status}`) })
        return r.json()
      })
      .then((d: any) => {
        const md = d.data as MdData
        setData(md)
        // No own menu → "Brands you carry" is meaningless; fall back to All.
        if (!md.has_own_menu) setShow('all')
      })
      .catch((e: any) => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [selectedLocation, sort, rival, category, brand, authFetch])

  useEffect(() => { load() }, [load])

  const rivalName = (id: string) => data?.by_rival.find(r => r.competitor_id === id)?.competitor_name ?? 'Rival'

  const toggleBrand = (key: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })

  // Client-side "Brands you carry" filter over the server's brand groups.
  // Whole-brand markdowns (a rival discounting a brand's entire catalogue) are
  // no longer a wall of chips — the toggle below narrows the list to just those brands.
  const wholeBrandSet = new Set((data?.whole_brand ?? []).map(w => w.brand))
  const groups = (data?.brand_groups ?? [])
    .filter(g => show === 'all' || g.operator_carries)
    .filter(g => !wholeBrandOnly || (g.brand != null && wholeBrandSet.has(g.brand)))
  const hasOwn = data?.has_own_menu ?? false
  const topPressureGroup = [...groups]
    .filter(g => g.operator_carries && g.rivals_cheaper_than_you > 0)
    .sort((a, b) =>
      b.rivals_cheaper_than_you - a.rivals_cheaper_than_you ||
      b.sku_count - a.sku_count ||
      b.best_pct_off - a.best_pct_off,
    )[0] ?? null
  const topGapSku = topPressureGroup
    ? [...topPressureGroup.skus]
        .filter(s => s.rival_cheaper && s.vs_you_dollar != null)
        .sort((a, b) => (a.vs_you_dollar ?? 0) - (b.vs_you_dollar ?? 0))[0] ?? null
    : null
  const pressureEvidenceAge = topPressureGroup ? ageLabel(topPressureGroup.latest_collected_at) : null
  const pressureEvidenceIsStale = topPressureGroup
    ? Date.now() - new Date(topPressureGroup.latest_collected_at).getTime() > 48 * 3_600_000
    : false

  const reviewTopPressure = () => {
    if (!topPressureGroup) return
    setExpanded(prev => new Set(prev).add(topPressureGroup.brand ?? ' unbranded'))
  }

  return (
    <div>
      {data && topPressureGroup && (
        <section className="price-pressure-brief" data-testid="pricedrops-pressure-brief">
          <div className="price-pressure-brief__content">
            <div className="price-pressure-brief__eyebrow">Highest observed price pressure</div>
            <div className="price-pressure-brief__answer">
              {topPressureGroup.brand_label} is undercut by {topPressureGroup.rivals_cheaper_than_you}{' '}
              rival{topPressureGroup.rivals_cheaper_than_you === 1 ? '' : 's'}.
            </div>
            <div className="price-pressure-brief__evidence">
              {topGapSku && topGapSku.vs_you_dollar != null
                ? `${topGapSku.competitor_name} is ${fmtMoney(Math.abs(topGapSku.vs_you_dollar))} below your price on ${topGapSku.product}, following a ${topGapSku.pct_off}% markdown.`
                : `${topPressureGroup.sku_count.toLocaleString('en-US')} marked-down competitor listings are associated with this carried brand; the deepest observed markdown is ${topPressureGroup.best_pct_off}%.`}
            </div>
            <div className="price-pressure-brief__basis">
              Ranked by rivals cheaper than you, marked-down listings, and deepest observed markdown · latest evidence {pressureEvidenceAge}
            </div>
            {pressureEvidenceIsStale && (
              <div className="price-pressure-brief__stale" role="status">
                This evidence is stale. Verify collection health before changing price.
              </div>
            )}
          </div>
          <div className="price-pressure-brief__action">
            <span>Recommended next step</span>
            <strong>Review the affected products before changing price.</strong>
            <button type="button" onClick={reviewTopPressure}>Review affected products →</button>
          </div>
        </section>
      )}

      {/* Controls */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '8px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', boxShadow: 'var(--card-shadow)' }}>
        {locations.length > 1 && (
          <Dropdown label="Location" value={locations.find(l => l.id === selectedLocation)?.name ?? 'Select'}>
            {(close) => locations.map(l => (
              <Opt key={l.id} selected={l.id === selectedLocation} onClick={() => { onSelectLocation(l.id); localStorage.setItem('promotions_location', l.id); close() }}>{l.name}</Opt>
            ))}
          </Dropdown>
        )}
        <Segmented
          value={show}
          onChange={(v) => setShow(v as Show)}
          disabled={!hasOwn}
          options={[{ value: 'carried', label: 'Brands you carry' }, { value: 'all', label: 'All brands' }]}
        />
        <Dropdown label="Sort" value={SORT_LABEL[sort]}>
          {(close) => (['undercut', 'pct', 'dollar', 'newest'] as Sort[]).map(s => (
            <Opt key={s} selected={sort === s} onClick={() => { setSort(s); close() }}>{SORT_LABEL[s]}</Opt>
          ))}
        </Dropdown>
        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
        <Dropdown label="Rival" value={rival ? rivalName(rival) : 'All rivals'}>
          {(close) => (
            <>
              <Opt selected={!rival} onClick={() => { setRival(''); close() }}>All rivals</Opt>
              {(data?.by_rival ?? []).map(r => (
                <Opt key={r.competitor_id} selected={rival === r.competitor_id} onClick={() => { setRival(r.competitor_id); close() }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.competitor_name}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>{r.count}</span>
                </Opt>
              ))}
            </>
          )}
        </Dropdown>
        <Dropdown label="Category" value={category || 'All'}>
          {(close) => (
            <>
              <Opt selected={!category} onClick={() => { setCategory(''); close() }}>All categories</Opt>
              {(data?.categories ?? []).map(c => (
                <Opt key={c} selected={category === c} onClick={() => { setCategory(c); close() }}>{c}</Opt>
              ))}
            </>
          )}
        </Dropdown>
        <Dropdown label="Brand" value={brand || 'All'}>
          {(close) => (
            <>
              <Opt selected={!brand} onClick={() => { setBrand(''); close() }}>All brands</Opt>
              {(data?.brands ?? []).map(b => (
                <Opt key={b} selected={brand === b} onClick={() => { setBrand(b); close() }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b}</span>
                </Opt>
              ))}
            </>
          )}
        </Dropdown>
        {data && data.whole_brand.length > 0 && (
          <button
            data-testid="pricedrops-wholebrand-chip"
            aria-pressed={wholeBrandOnly}
            onClick={() => setWholeBrandOnly(v => !v)}
            title="Rivals that marked down a brand's entire catalogue"
            style={{
              background: wholeBrandOnly ? 'var(--accent)' : 'var(--surface-2)',
              border: `1px solid ${wholeBrandOnly ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
              fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600,
              color: wholeBrandOnly ? '#fff' : 'var(--text-2)', whiteSpace: 'nowrap',
            }}
          >
            Whole-brand deals ({data.whole_brand.length})
          </button>
        )}
        <div data-testid="pricedrops-count" style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
          {data ? `${groups.length.toLocaleString('en-US')} brand${groups.length === 1 ? '' : 's'} · ${data.filtered_skus.toLocaleString('en-US')} price drop${data.filtered_skus === 1 ? '' : 's'}` : ''}
        </div>
      </div>

      {/* What-this-is note (honest: facts, no cause) */}
      <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.55, marginBottom: 14 }}>
        Rival SKUs whose current menu price sits below their regular price — grouped by brand and ranked by
        how many of your tracked rivals now undercut your own price. Observed facts only; we don't assert
        why a price dropped.
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>
          Pulling current price drops across your tracked rivals…
        </div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', marginBottom: 8 }}>Couldn't load price drops for this location.</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', display: 'inline-block', maxWidth: 480 }}>{error}</div>
          <div style={{ marginTop: 12 }}>
            <button onClick={load} style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
          </div>
        </div>
      ) : !selectedLocation ? (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>Select a location to see rival price drops.</div>
      ) : data && data.total_skus === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>
          No active price drops across your tracked rivals right now. Every SKU is at its regular price.
        </div>
      ) : data ? (
        <>
          {/* KPI strip */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatTile size="sm" value={String(data.kpis.brands_discounted)} label="Brands discounted" style={{ flex: 1, minWidth: 130 }} />
            <StatTile size="sm" value={String(data.kpis.rivals_discounting)} label="Rivals discounting" style={{ flex: 1, minWidth: 130 }} />
            <StatTile
              size="sm"
              tone="danger"
              value={hasOwn ? String(data.kpis.you_carry_and_undercut) : '—'}
              label={hasOwn ? 'You carry & undercut' : 'You carry & undercut · no own menu'}
              style={{ flex: 1.3, minWidth: 160, outline: '1px solid var(--danger-soft)', outlineOffset: -1 }}
            />
            <StatTile
              size="sm"
              tone="warm"
              value={data.kpis.biggest_drop ? `−${data.kpis.biggest_drop.pct}%` : '—'}
              label={data.kpis.biggest_drop ? `Biggest drop · ${data.kpis.biggest_drop.competitor_name}` : 'Biggest drop'}
              style={{ flex: 1.3, minWidth: 160 }}
            />
          </div>

          {/* Brand-first list */}
          {groups.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>
              {show === 'carried'
                ? 'No price drops on brands you carry match these filters. Switch to “All brands” to see the rest.'
                : 'No price drops match these filters.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {groups.map((g) => {
                const key = g.brand ?? ' unbranded'
                return (
                  <BrandRow
                    key={key}
                    g={g}
                    hasOwnMenu={hasOwn}
                    totalRivals={data.total_rivals}
                    open={expanded.has(key)}
                    onToggle={() => toggleBrand(key)}
                  />
                )
              })}
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}
